import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';

export const BankTransferSettings = () => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.BANK_TRANSFER)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.BANK_TRANSFER, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings)
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />
      <Typography
        sx={{
          ...sharedStyles.h5,
          fontWeight: 'bold',
        }}
      >
        Banka Transferi
      </Typography>
      <Divider />

      <SettingsRow
        name='Aktif'
        valueType={SettingsValueType.BOOLEAN}
        value={getSettingsValue(settings, 'bank_transfer_enabled')}
        onChange={(newValue) => handleChange('bank_transfer_enabled', newValue)}
      />
      <SettingsRow
        name='Başlık'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'bank_transfer_label')}
        onChange={(newValue) => handleChange('bank_transfer_label', newValue)}
      />
      <SettingsRow
        name='Açıklama'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'bank_transfer_description')}
        onChange={(newValue) =>
          handleChange('bank_transfer_description', newValue)
        }
        multiline
      />
      <SettingsRow
        name='Alıcı Adı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'bank_transfer_recipient_name')}
        onChange={(newValue) =>
          handleChange('bank_transfer_recipient_name', newValue)
        }
      />
      <SettingsRow
        name='IBAN'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'bank_transfer_iban')}
        onChange={(newValue) => handleChange('bank_transfer_iban', newValue)}
      />
      <SettingsRow
        name='Banka Adı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'bank_transfer_bank_name')}
        onChange={(newValue) =>
          handleChange('bank_transfer_bank_name', newValue)
        }
      />
      <SettingsRow
        name='Şube'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'bank_transfer_bank_branch')}
        onChange={(newValue) =>
          handleChange('bank_transfer_bank_branch', newValue)
        }
      />
      <SettingsRow
        name='Hesap Numarası'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'bank_transfer_account_no')}
        onChange={(newValue) =>
          handleChange('bank_transfer_account_no', newValue)
        }
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
